import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PRODUCTS } from "../../data/products";

const ProductCard = ({ product, index }) => {
  const isOffset = index % 2 === 1;
  return (
    <motion.a
      href={product.href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={product.testId}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-120px" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative block overflow-hidden rounded-3xl border border-white/10 bg-[#0a0a0a] ${
        isOffset ? "md:mt-24" : ""
      }`}
    >
      {/* Image */}
      <div
        className={`relative overflow-hidden ${
          product.fullImage ? "aspect-[3/4] bg-black" : "aspect-[4/5] max-h-[70vh]"
        }`}
      >
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className={`h-full w-full transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105 ${
            product.fullImage ? "object-contain" : "object-cover"
          }`}
        />

        {!product.fullImage && (
          <>
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            {/* Index label */}
            <span className="absolute left-6 top-5 font-mono-accent text-xs uppercase tracking-[0.2em] text-white/60">
              {product.index} — {product.category}
            </span>
            {/* Bottom content overlay */}
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
              <h3 className="font-display text-4xl font-extrabold tracking-tighter text-white md:text-5xl">
                {product.name}
              </h3>
              <p className="mt-2 max-w-sm text-sm leading-relaxed text-zinc-300">
                {product.tagline}
              </p>
            </div>
          </>
        )}

        {/* 10% OFF badge */}
        <div
          data-testid={`${product.id}-discount-badge`}
          className="absolute right-5 top-5 z-10 rounded-full bg-[#ccff00] px-4 py-2 font-mono-accent text-xs font-bold uppercase tracking-[0.15em] text-black"
        >
          10% OFF
        </div>
      </div>

      {/* Footer bar */}
      <div className="flex items-center justify-between px-6 py-6 md:px-8">
        <div className="max-w-md">
          {product.fullImage && (
            <h3 className="mb-1 font-display text-2xl font-extrabold tracking-tighter text-white md:text-3xl">
              {product.name}
            </h3>
          )}
          <p className="text-sm leading-relaxed text-zinc-500">
            {product.description}
          </p>
        </div>
        <span className="ml-4 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-white/20 transition-colors duration-300 group-hover:border-[#ccff00] group-hover:bg-[#ccff00]">
          <ArrowUpRight className="h-6 w-6 text-white transition-colors duration-300 group-hover:text-black" />
        </span>
      </div>
    </motion.a>
  );
};

export const Products = () => {
  return (
    <section
      id="drops"
      data-testid="products-section"
      className="relative border-t border-white/10 px-6 py-24 md:px-10 md:py-32"
    >
      <div className="mx-auto max-w-7xl">
        <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="mb-4 font-mono-accent text-xs uppercase tracking-[0.25em] text-zinc-500">
              / The Drops
            </p>
            <h2 className="font-display text-5xl font-extrabold tracking-tighter text-white md:text-7xl">
              Claim your
              <br />
              <span className="text-[#ccff00]">discount.</span>
            </h2>
          </div>
          <p className="max-w-xs text-sm leading-relaxed text-zinc-400">
            Three drops, one mission. Tap a card to open the official store with
            your 10% off already locked in.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-3 md:gap-8">
          {PRODUCTS.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
